
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Download, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useState } from "react";

export const DashboardAnalytics = () => {
  const [isDeleting, setIsDeleting] = useState(false);

  const { data: stats, isLoading, refetch } = useQuery({
    queryKey: ["professional-analytics"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const [appointmentsResult, purchaseRequestsResult] = await Promise.all([
        supabase
          .from("professional_appointments")
          .select("created_at, status")
          .eq("professional_id", user.id),
        supabase
          .from("product_purchase_requests")
          .select("created_at, status")
          .eq("professional_id", user.id),
      ]);

      // Group appointments by month
      const appointmentsByMonth = (appointmentsResult.data || []).reduce((acc: any, curr) => {
        const month = new Date(curr.created_at).toLocaleString('fr-FR', { month: 'long' });
        acc[month] = (acc[month] || 0) + 1;
        return acc;
      }, {});

      // Group purchase requests by month
      const purchaseRequestsByMonth = (purchaseRequestsResult.data || []).reduce((acc: any, curr) => {
        const month = new Date(curr.created_at).toLocaleString('fr-FR', { month: 'long' });
        acc[month] = (acc[month] || 0) + 1;
        return acc;
      }, {});

      // Combine data for chart
      const allMonths = Array.from(new Set([...Object.keys(appointmentsByMonth), ...Object.keys(purchaseRequestsByMonth)]));
      const mois = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
      allMonths.sort((a, b) => mois.indexOf(a.toLowerCase()) - mois.indexOf(b.toLowerCase()));

      const chartData = allMonths.map(month => ({
        name: month,
        appointments: appointmentsByMonth[month] || 0,
        purchaseRequests: purchaseRequestsByMonth[month] || 0,
      }));

      return {
        chartData,
        totalAppointments: appointmentsResult.data?.length || 0,
        totalPurchaseRequests: purchaseRequestsResult.data?.length || 0,
      };
    },
  });

  const handleDeleteData = async () => {
    try {
      setIsDeleting(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      await Promise.all([
        supabase
          .from("professional_appointments")
          .delete()
          .eq("professional_id", user.id),
        supabase
          .from("professional_patients")
          .delete()
          .eq("professional_id", user.id),
      ]);

      toast.success("Données supprimées avec succès");
      refetch();
    } catch (error) {
      toast.error("Erreur lors de la suppression des données");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDownloadReport = () => {
    if (!stats) return;

    const csvContent = [
      ["Mois", "Rendez-vous", "Demandes d'achat"],
      ...stats.chartData.map(data => [
        data.name,
        data.appointments,
        data.purchaseRequests,
      ]),
    ]
      .map(row => row.join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "rapport-statistiques.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return <Card className="w-full h-[400px] animate-pulse" />;
  }

  return (
    <Card className="w-full">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium">
          Statistiques mensuelles
        </CardTitle>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownloadReport}
            disabled={isDeleting}
          >
            <Download className="h-4 w-4 mr-2" />
            Télécharger
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={handleDeleteData}
            disabled={isDeleting}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Supprimer
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="h-[300px] mt-4">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={stats?.chartData || []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="appointments" name="Rendez-vous" fill="#4f46e5" />
              <Bar dataKey="purchaseRequests" name="Demandes d'achat" fill="#ec4899" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};
